import * as THREE from 'three';
import Application from '../Application';

const SCREEN_MESH_NAME_VARIANTS = [
    'et.001',
    'et001',
    'et_001',
    'eT.001',
    'ET.001',
    'Et.001',
    'et.001_screen',
    'screen',
    'Screen',
];

interface PbrSet {
    baseColor: THREE.Texture;
    normal: THREE.Texture;
    roughness: THREE.Texture;
    metallic: THREE.Texture;
}

// 你 GLB 里实际的材质名是 '08 - Default' / '09 - Default'（不带 pc_ 前缀）。
// 贴图文件名是 pc_08_*.png（键盘）/ pc_09_*.png（机身）。
// 经查 GLB：mesh 'et'（机身）+ 'et.001'（屏幕）都用 '09 - Default' 材质 → 应绑 pc_09 贴图；
//          mesh 'Object'（键盘）用 '08 - Default' 材质 → 应绑 pc_08 贴图。
// 所以是 identity 映射；要翻就翻这里。
const MATERIAL_TO_TEX_MAP: Record<'pc08' | 'pc09', 'pc08' | 'pc09'> = {
    pc08: 'pc08',
    pc09: 'pc09',
};

// 同时兼容 'pc_08_-_Default' / '08 - Default' / 'PC08' 等多种命名。
// 用边界匹配防止误吞 '108' 之类的数字。
const detectMaterialSet = (matName: string): 'pc08' | 'pc09' | null => {
    if (!matName) return null;
    const lower = matName.toLowerCase();
    if (/(?:^|[^0-9])08(?:[^0-9]|$)/.test(lower)) return 'pc08';
    if (/(?:^|[^0-9])09(?:[^0-9]|$)/.test(lower)) return 'pc09';
    return null;
};

const matchesScreenName = (name: string): boolean => {
    if (!name) return false;
    if (SCREEN_MESH_NAME_VARIANTS.includes(name)) return true;
    const normalized = name.toLowerCase().replace(/[._\s-]/g, '');
    return normalized === 'et001' || normalized === 'screen';
};

export default class Computer {
    application: Application;
    scene: THREE.Scene;
    root: THREE.Object3D | null = null;
    screenMesh: THREE.Mesh | null = null;
    onScreenReady?: () => void;

    constructor() {
        this.application = new Application();
        this.scene = this.application.scene;
        this.application.resources.on('ready', () => this.build());
    }

    private build() {
        const gltf = this.application.resources.items.gltfModel.computerModel;
        if (!gltf) {
            console.warn('[Computer] computerModel resource missing');
            return;
        }
        const root = gltf.scene;
        const sets = this.collectPbrSets();
        const fallbackSet = sets.pc08 ?? sets.pc09;

        const meshLog: { name: string; mat: string; assigned: string }[] = [];

        root.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) return;

            const matName =
                Array.isArray(child.material)
                    ? (child.material[0]?.name ?? '')
                    : (child.material as THREE.Material | undefined)?.name ?? '';

            const setKey = detectMaterialSet(matName);
            const mappedKey = setKey ? MATERIAL_TO_TEX_MAP[setKey] : null;
            const set = (mappedKey && sets[mappedKey]) || fallbackSet;
            let assigned = 'NONE';

            if (set) {
                const newMat = new THREE.MeshStandardMaterial({
                    map: set.baseColor,
                    normalMap: set.normal,
                    roughnessMap: set.roughness,
                    metalnessMap: set.metallic,
                    metalness: 1.0,
                    roughness: 1.0,
                    envMapIntensity: 0.9,
                });
                if (Array.isArray(child.material)) {
                    child.material.forEach((m) => m.dispose?.());
                } else {
                    (child.material as THREE.Material).dispose?.();
                }
                child.material = newMat;
                assigned = setKey
                    ? `mat:${setKey}→tex:${mappedKey}`
                    : `fallback:tex:${sets.pc08 ? 'pc08' : 'pc09'}`;
            }

            child.castShadow = true;
            child.receiveShadow = true;

            if (matchesScreenName(child.name)) {
                this.screenMesh = child;
            }

            meshLog.push({ name: child.name, mat: matName, assigned });
        });

        console.info(
            '[Computer] meshes loaded:',
            meshLog.map((m) => `${m.name}<${m.mat || 'unnamed'}>=${m.assigned}`).join(' | ')
        );

        if (!this.screenMesh) {
            console.warn(
                '[Computer] screen mesh not found. Tried variants:',
                SCREEN_MESH_NAME_VARIANTS,
                'Available mesh names:',
                meshLog.map((m) => m.name).join(', ')
            );
        } else {
            console.info('[Computer] screen mesh detected:', this.screenMesh.name);
        }

        // Center & scale the model so its tallest dimension is ~2 meters
        const box = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const targetHeight = 2.0;
        const scale = targetHeight / Math.max(size.y, 0.0001);
        root.scale.setScalar(scale);
        root.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);

        this.root = root;
        this.scene.add(root);

        if (this.onScreenReady) this.onScreenReady();
    }

    private collectPbrSets(): { pc08: PbrSet | null; pc09: PbrSet | null } {
        const t = this.application.resources.items.texture;
        const make = (prefix: 'pc08' | 'pc09'): PbrSet | null => {
            const bc = t[`${prefix}_baseColor`];
            const n = t[`${prefix}_normal`];
            const r = t[`${prefix}_roughness`];
            const m = t[`${prefix}_metallic`];
            if (!bc || !n || !r || !m) {
                console.warn(`[Computer] ${prefix} texture set incomplete`, {
                    baseColor: !!bc,
                    normal: !!n,
                    roughness: !!r,
                    metallic: !!m,
                });
                return null;
            }
            bc.encoding = THREE.sRGBEncoding;
            bc.flipY = false;
            n.flipY = false;
            r.flipY = false;
            m.flipY = false;
            bc.wrapS = bc.wrapT = THREE.RepeatWrapping;
            n.wrapS = n.wrapT = THREE.RepeatWrapping;
            r.wrapS = r.wrapT = THREE.RepeatWrapping;
            m.wrapS = m.wrapT = THREE.RepeatWrapping;
            return { baseColor: bc, normal: n, roughness: r, metallic: m };
        };
        return { pc08: make('pc08'), pc09: make('pc09') };
    }
}
